import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import type { SourceStatus } from '@/hooks/integrations/meeting-summaries-sources-status';
import { isSourceConnected } from '@/hooks/integrations/meeting-summaries-sources-status';
import {
  Accordion,
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';
import type { MeetingSummarySourceConfig } from '@/lib/integrations/meeting-summaries/source-config';
import { formatRelativeTime } from '@/lib/utils/timeFormatting';

import { FathomActions } from './FathomActions';
import { FirefliesActions } from './FirefliesActions';
import { GranolaActions } from './GranolaActions';

export interface MeetingSummarySourceCardProps {
  orgId: number;
  config: MeetingSummarySourceConfig;
  status: SourceStatus;
  canConnect: boolean;
  canModify: boolean;
}

const BADGE_CONFIG = {
  connected_with_data: {
    label: 'Receiving summaries',
    color: 'success' as const,
  },
  connected_no_data: {
    label: 'Waiting for first summary',
    color: 'gray' as const,
  },
  auth_failed: { label: 'Reconnect needed', color: 'warning' as const },
  disconnected: { label: 'Not connected', color: 'gray' as const },
  no_delivery: { label: 'Summaries not arriving', color: 'warning' as const },
  stale: { label: 'No recent summaries', color: 'gray' as const },
};

const STALENESS_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000;

interface ConnectionMetaProps {
  orgId: number;
  config: MeetingSummarySourceConfig;
  status: SourceStatus;
}

/**
 * Connection-meta line shown on connected cards.
 *
 * Google Meet & Zoom render an EntityBadge for the linked email integration
 * (provider-agnostic copy on Zoom, since users may eventually connect
 * non-Gmail email integrations). Fathom and Granola render text-only.
 */
function ConnectionMeta({ orgId, config, status }: ConnectionMetaProps) {
  const navigate = useNavigate();

  const handleIntegrationBadgeClick = useCallback(() => {
    navigate(`/${orgId}/settings/email-calendar`);
  }, [navigate, orgId]);

  // Includes connected_no_delivery: the account line and last-summary time are
  // exactly what a user needs to self-diagnose when summaries aren't landing.
  if (!isSourceConnected(status)) {
    return null;
  }
  const { meta, last_received_at } = status;
  const lastSummary = last_received_at ? (
    <> · Last summary {formatRelativeTime(last_received_at)}</>
  ) : null;

  if (
    (config.id === 'google_meet' || config.id === 'zoom') &&
    meta.integration_entity_id
  ) {
    const prefix =
      config.id === 'google_meet'
        ? 'Reading from Google Workspace · '
        : 'Reading from email integration · ';
    return (
      <div className='flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-[var(--color-text-secondary)]'>
        <span>{prefix}</span>
        <EntityBadgeById
          entityId={meta.integration_entity_id}
          orgId={orgId}
          showLeading={false}
          onClick={handleIntegrationBadgeClick}
          fallbackDisplayName={meta.email}
          fallbackEntityType='ahoy_external_integration'
        />
        {lastSummary && <span>{lastSummary}</span>}
      </div>
    );
  }

  let base: string;
  switch (config.id) {
    case 'fathom':
      base = `Connected as ${meta.email ?? ''}`;
      break;
    case 'google_meet':
      // Fallback if integration_entity_id is missing (legacy / partial data).
      base = `Reading from Google Workspace · ${meta.email ?? ''}`;
      break;
    case 'zoom':
      base = `Reading from email integration · ${meta.email ?? ''}`;
      break;
    case 'granola':
      base = `Connected as ${meta.email ?? ''}`;
      break;
    case 'fireflies':
      base = `Connected as ${meta.email ?? ''}`;
      break;
    default:
      return null;
  }
  return (
    <p className='text-sm text-[var(--color-text-secondary)]'>
      {base}
      {lastSummary}
    </p>
  );
}

interface ActionAreaProps {
  orgId: number;
  config: MeetingSummarySourceConfig;
  status: SourceStatus;
  canConnect: boolean;
  canModify: boolean;
  cardHeadingId: string;
}

function ActionArea({
  orgId,
  config,
  status,
  canConnect,
  canModify,
  cardHeadingId,
}: ActionAreaProps) {
  if (config.action.kind === 'oauth') {
    let Actions: typeof FathomActions;
    if (config.id === 'granola') {
      Actions = GranolaActions;
    } else if (config.id === 'fireflies') {
      Actions = FirefliesActions;
    } else {
      Actions = FathomActions;
    }
    return (
      <Actions
        orgId={orgId}
        status={status}
        canConnect={canConnect}
        canModify={canModify}
      />
    );
  }

  const { label, settingsPath } = config.action;

  if (isSourceConnected(status)) {
    return null;
  }

  const isReconnect = status.connection_status === 'auth_failed';
  const linkText = isReconnect ? `Reconnect ${label}` : `Connect ${label}`;
  const isAllowed = isReconnect ? canModify : canConnect;
  const helpId = `${cardHeadingId}-action-help`;

  if (!isAllowed) {
    return (
      <div className='space-y-1'>
        <Button
          color='primary'
          size='sm'
          isDisabled={true}
          aria-describedby={helpId}
        >
          {linkText}
        </Button>
        <p id={helpId} className='text-xs text-[var(--color-text-secondary)]'>
          {isReconnect
            ? 'Only admins can reconnect integrations'
            : 'Only admins can connect integrations'}
        </p>
      </div>
    );
  }

  return (
    <Button
      color='primary'
      size='sm'
      href={`/${orgId}/${settingsPath}`}
      aria-describedby={cardHeadingId}
    >
      {linkText}
    </Button>
  );
}

export function MeetingSummarySourceCard({
  orgId,
  config,
  status,
  canConnect,
  canModify,
}: MeetingSummarySourceCardProps) {
  const queryClient = useQueryClient();
  const cardHeadingId = `card-heading-${config.id}`;

  // For error state, show disconnected fallback everywhere - badge, connection
  // meta, and action area - so the card never shows "connected" while actions
  // are treated as disconnected.
  const effectiveStatus = useMemo(
    () =>
      status.error === 'fetch_failed'
        ? { ...status, connection_status: 'disconnected' as const }
        : status,
    [status]
  );

  const cardState = useMemo(() => {
    const cs = effectiveStatus.connection_status;
    if (cs === 'disconnected') {
      return 'disconnected' as const;
    }
    if (cs === 'auth_failed') {
      return 'auth_failed' as const;
    }
    if (cs === 'connected_no_delivery') {
      return 'no_delivery' as const;
    }
    if (!effectiveStatus.last_received_at) {
      return 'waiting' as const;
    }
    const lastMs = new Date(effectiveStatus.last_received_at).getTime();
    return Date.now() - lastMs > STALENESS_THRESHOLD_MS
      ? ('stale' as const)
      : ('receiving' as const);
  }, [effectiveStatus.connection_status, effectiveStatus.last_received_at]);

  const badgeConfig = useMemo(() => {
    switch (cardState) {
      case 'receiving':
        return BADGE_CONFIG.connected_with_data;
      case 'waiting':
        return BADGE_CONFIG.connected_no_data;
      case 'stale':
        return BADGE_CONFIG.stale;
      case 'no_delivery':
        return BADGE_CONFIG.no_delivery;
      case 'auth_failed':
        return BADGE_CONFIG.auth_failed;
      default:
        return BADGE_CONFIG.disconnected;
    }
  }, [cardState]);

  const showStalenessWarning = cardState === 'stale';
  const showNoDeliveryMessage = cardState === 'no_delivery';

  const handleRetry = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['meetingSummariesSourcesStatus', orgId],
    });
  }, [queryClient, orgId]);

  const isDisconnected = effectiveStatus.connection_status === 'disconnected';

  return (
    <div className='overflow-hidden rounded-xl border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)]'>
      <div className='space-y-3 p-4'>
        {/* Icon, heading row with badge */}
        <div className='flex items-start gap-3'>
          <div
            aria-hidden='true'
            className='flex size-10 shrink-0 items-center justify-center rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)]'
          >
            {config.icon}
          </div>
          <div className='min-w-0 flex-1'>
            <div className='flex flex-wrap items-start justify-between gap-x-3 gap-y-1'>
              <h3
                id={cardHeadingId}
                className='text-sm font-semibold text-[var(--color-text-primary)]'
              >
                {config.name}
              </h3>
              <Badge
                color={badgeConfig.color}
                type='pill-color'
                size='sm'
                className='shrink-0'
              >
                {badgeConfig.label}
              </Badge>
            </div>
            <p className='mt-0.5 text-sm text-[var(--color-text-secondary)]'>
              {config.subCopy}
            </p>
          </div>
        </div>

        {/* Connection meta line - only when connected */}
        <ConnectionMeta
          orgId={orgId}
          config={config}
          status={effectiveStatus}
        />

        {/* Soft staleness warning (>7 days since last summary) */}
        {showStalenessWarning && (
          <p className='text-sm text-utility-warning-700'>
            {`It's been a while since your last ${config.name} summary – confirm your setup hasn't changed.`}
          </p>
        )}

        {/* Connected, but summaries aren't arriving - mutually exclusive with staleness warning */}
        {showNoDeliveryMessage && (
          <p className='text-sm text-utility-warning-700'>
            {`${config.name} is connected, but recent recordings haven't become summaries in Ahoy. Reconnecting won't help.`}
          </p>
        )}

        {/* Fetch-error warning row - inline, above action area */}
        {status.error === 'fetch_failed' && (
          <div className='flex items-center justify-between gap-3 rounded-lg border border-utility-warning-200 bg-utility-warning-50 px-3 py-2'>
            <p className='text-sm text-utility-warning-700'>
              We couldn't load this source's status. Try again.
            </p>
            <Button color='secondary' size='sm' onClick={handleRetry}>
              Retry
            </Button>
          </div>
        )}

        {/* Action area */}
        <ActionArea
          orgId={orgId}
          config={config}
          status={effectiveStatus}
          canConnect={canConnect}
          canModify={canModify}
          cardHeadingId={cardHeadingId}
        />
      </div>

      {/* Setup checklist accordion */}
      <Accordion
        className='border-t border-[var(--color-border-secondary)]'
        defaultExpandedKeys={isDisconnected ? ['setup-checklist'] : []}
      >
        <AccordionItem id='setup-checklist'>
          <AccordionTrigger className='w-full flex items-center justify-between gap-2 px-4 py-3 text-left text-sm font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-tertiary)]'>
            <span>What you&apos;ll need</span>
            <AccordionChevron />
          </AccordionTrigger>
          <AccordionPanel className='px-4 pb-4 text-sm text-[var(--color-text-secondary)]'>
            <ul className='list-inside list-disc space-y-1'>
              {config.setupChecklist.items.map(item => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            {config.setupChecklist.tip && (
              <p className='mt-2'>
                <strong>Tip -</strong> {config.setupChecklist.tip}
              </p>
            )}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
